import { createLogger } from '@/utils/logger'
import { ref } from 'vue'
import { getConfig } from '@/config'

const fileLogger = createLogger('useCookieConsent')

const COOKIE_CONSENT_KEY = 'cookie_consent'
const COOKIE_MAX_AGE_DAYS = 365

type ConsentValue = 'accepted' | 'rejected' | null

// Singleton state so all components share the same consent and we only read storage once
let consentChecked = false
const hasConsented = ref<boolean>(false)
const consentValue = ref<ConsentValue>(null)

function getStorageKey(): string {
  return `${getConfig().app.id}_${COOKIE_CONSENT_KEY}`
}

function isValidConsent(value: unknown): value is ConsentValue {
  return value === 'accepted' || value === 'rejected'
}

function readFromCookie(cookieKey: string): ConsentValue | null {
  if (typeof document === 'undefined' || !document.cookie) return null
  try {
    const escaped = cookieKey.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${escaped}=([^;]*)`))
    const raw = match ? decodeURIComponent(match[1].trim()) : null
    if (!raw) return null
    const parsed = JSON.parse(raw) as unknown
    return isValidConsent(parsed) ? parsed : null
  } catch {
    return null
  }
}

function writeCookie(cookieKey: string, value: string): void {
  if (typeof document === 'undefined') return
  const maxAge = COOKIE_MAX_AGE_DAYS * 24 * 60 * 60
  document.cookie = `${cookieKey}=${encodeURIComponent(value)};path=/;max-age=${maxAge};SameSite=Lax`
}

function clearCookie(cookieKey: string): void {
  if (typeof document === 'undefined') return
  document.cookie = `${cookieKey}=;path=/;max-age=0`
}

function readConsentFromStorage(storageKey: string): ConsentValue | null {
  try {
    if (typeof localStorage === 'undefined') return readFromCookie(storageKey)
    const stored = localStorage.getItem(storageKey)
    if (stored) {
      const parsed = JSON.parse(stored) as unknown
      return isValidConsent(parsed) ? parsed : null
    }
  } catch (error) {
    fileLogger.warn('localStorage read failed, trying cookie', error)
  }
  return readFromCookie(storageKey)
}

function writeConsentToStorage(storageKey: string, value: string): void {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(storageKey, JSON.stringify(value))
    }
  } catch (error) {
    fileLogger.warn('localStorage write failed, using cookie', error)
  }
  writeCookie(storageKey, JSON.stringify(value))
}

function clearConsentStorage(storageKey: string): void {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(storageKey)
    }
  } catch {
    // ignore
  }
  clearCookie(storageKey)
}

/**
 * Composable for managing GDPR cookie consent.
 * Uses singleton state so consent is shared and only read from storage once per page load.
 * Falls back to a cookie when localStorage is blocked (e.g. Safari, iframes).
 */
export function useCookieConsent() {
  const config = getConfig()
  const storageKey = `${config.app.id}_${COOKIE_CONSENT_KEY}`

  /**
   * Check if user has already provided consent (localStorage then cookie fallback)
   */
  const checkConsent = (): boolean => {
    if (consentChecked) {
      return hasConsented.value
    }
    consentChecked = true
    const stored = readConsentFromStorage(storageKey)
    if (stored) {
      consentValue.value = stored
      hasConsented.value = true
      fileLogger.log(`Cookie consent found: ${stored}`)
      return true
    }
    hasConsented.value = false
    consentValue.value = null
    return false
  }

  /**
   * Accept cookies and store consent
   */
  const acceptCookies = (): void => {
    const consent = 'accepted'
    writeConsentToStorage(storageKey, consent)
    consentValue.value = consent
    hasConsented.value = true
    fileLogger.log('Cookie consent accepted and stored')
  }

  /**
   * Reject cookies and store consent
   */
  const rejectCookies = (): void => {
    const consent = 'rejected'
    writeConsentToStorage(storageKey, consent)
    consentValue.value = consent
    hasConsented.value = true
    fileLogger.log('Cookie consent rejected and stored')
  }

  /**
   * Clear consent (for testing or user preference changes)
   */
  const clearConsent = (): void => {
    clearConsentStorage(storageKey)
    hasConsented.value = false
    consentValue.value = null
    consentChecked = false
    fileLogger.log('Cookie consent cleared')
  }

  // Run check once per page load (singleton)
  checkConsent()

  return {
    hasConsented,
    consentValue,
    acceptCookies,
    rejectCookies,
    clearConsent,
    checkConsent
  }
}

